import React, {useCallback, useEffect, useRef, useState} from "react";
import Webcam from "react-webcam";
import * as faceapi from "face-api.js";
import {Button, Spin, message} from "antd";
import i18next from "i18next";

const iphoneButtonStyles = {
  outer: {
    width: "65px",
    height: "65px",
    borderRadius: "50%",
    border: "4px solid #fff",
    boxShadow: "0 0 0 1px #ccc",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    background: "transparent",
    padding: 0,
  },
  innerImage: {
    width: "55px",
    height: "55px",
    borderRadius: "50%",
    backgroundColor: "#4096ff",
    transition: "all 0.2s",
  },
  innerVideo: {
    width: "55px",
    height: "55px",
    borderRadius: "50%",
    backgroundColor: "red",
    transition: "all 0.2s",
  },
  innerRecording: {
    width: "30px",
    height: "30px",
    borderRadius: "8px",
    backgroundColor: "red",
    transition: "all 0.2s",
  },
};

const FaceCapturing = ({promptText = "", onComplete}) => {
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);

  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [isFaceAligned, setIsFaceAligned] = useState(false);

  const [stage, setStage] = useState("image");

  const [capturedImage, setCapturedImage] = useState(null);
  const chunksRef = useRef([]);
  const [capturedVideo, setCapturedVideo] = useState(null);
  const [isRecording, setIsRecording] = useState(false);

  const [recordingTime, setRecordingTime] = useState(0);
  const MAX_RECORDING_TIME = 30;

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prevTime) => {
          if (prevTime >= MAX_RECORDING_TIME) {
            clearInterval(interval);
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return prevTime + 0.1;
        });
      }, 100);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  useEffect(() => {
    const loadModels = async() => {
      const MODEL_URL = process.env.PUBLIC_URL + "/models";
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
      setModelsLoaded(true);
    };
    loadModels();
  }, []);

  const handleVideoOnPlay = () => {
    if (capturedImage || capturedVideo) {return;}

    const interval = setInterval(async() => {
      if (webcamRef.current && webcamRef.current.video && webcamRef.current.video.readyState === 4) {
        const video = webcamRef.current.video;
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        const centerX = videoWidth / 2;
        const centerY = videoHeight / 2;

        const detections = await faceapi.detectAllFaces(video, new faceapi.TinyFaceDetectorOptions());

        if (detections.length === 1) {
          const faceBox = detections[0].box;
          const faceCenterX = faceBox.x + faceBox.width / 2;
          const faceCenterY = faceBox.y + faceBox.height / 2;
          const distanceX = Math.abs(faceCenterX - centerX);
          const distanceY = Math.abs(faceCenterY - centerY);

          const isGoodSize = faceBox.width > 80 && faceBox.width < 300;
          const isCentered = distanceX < 50 && distanceY < 60;

          setIsFaceAligned(isCentered && isGoodSize);
        } else {
          setIsFaceAligned(false);
        }
      }
    }, 200);

    return () => clearInterval(interval);
  };

  const captureImage = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
  }, [webcamRef]);

  const retakeImage = () => {
    setCapturedImage(null);
  };

  const startRecording = useCallback(() => {
    chunksRef.current = []; // پاک کردن دیتای قبلی
    setIsRecording(true);

    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: "video/webm",
    });

    // جمع‌آوری تکه‌های ویدیو
    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    // پردازش ویدیو دقیقاً در لحظه توقف ضبط
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, {type: "video/webm"});
      const url = URL.createObjectURL(blob);
      setCapturedVideo(url);

      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const videoBase64 = reader.result;
        if (onComplete) {
          onComplete({
            photo_base64: capturedImage,
            video_base64: videoBase64,
          });
          message.success(i18next.t("general:Photo and video were saved successfully."));
        }
      };
      reader.onerror = () => {
        message.error(i18next.t("general:Video processing error"));
      };
    };

    mediaRecorderRef.current.start();
  }, [webcamRef, capturedImage, onComplete]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const retakeVideo = () => {
    setCapturedVideo(null);
    chunksRef.current = [];
    if (onComplete) {
      onComplete({
        photo_base64: capturedImage,
        video_base64: "",
      });
    }
  };
  return (
    <div style={{display: "flex", flexDirection: "column", alignItems: "center", marginTop: "20px", width: "100%"}}>
      {!modelsLoaded ? (
        <Spin description="" />
      ) : (
        <>
          {stage === "video" && !capturedVideo && (
            <div style={{marginBottom: "15px", padding: "10px", background: "#f0f0f0", borderRadius: "8px", width: "100%", maxWidth: "350px", textAlign: "center"}}>
              <p style={{margin: 0, fontWeight: ""}}>{i18next.t("general:Read the following text aloud")}:</p>
              <p style={{margin: 0, fontWeight: "bold"}}>{promptText}</p>
            </div>
          )}

          <div style={{position: "relative", width: "100%", maxWidth: "370px", aspectRatio: "1 / 1", display: "flex", justifyContent: "center", alignItems: "center"}}>
            {isRecording && (
              <svg
                viewBox="0 0 370 370"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  transform: "rotate(-90deg)",
                  zIndex: 2,
                }}
              >
                <circle
                  cx="185"
                  cy="185"
                  r="178"
                  fill="none"
                  stroke="#ff1317"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 178}
                  strokeDashoffset={(2 * Math.PI * 178) * (1 - recordingTime / MAX_RECORDING_TIME)}
                  style={{transition: "stroke-dashoffset 0.1s linear"}}
                />
              </svg>
            )}

            <div
              style={{
                position: "relative",
                width: "92%",
                height: "92%",
                borderRadius: "50%",
                overflow: "hidden",
                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                backgroundColor: "#000",
                zIndex: 1,
              }}
            >
              {stage === "image" && capturedImage ? (
                <img src={capturedImage} alt="Captured" style={{width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)"}} />
              ) : stage === "video" && capturedVideo ? (
                <video src={capturedVideo} loop autoPlay playsInline style={{width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)"}} />
              ) : (
                <>
                  <Webcam
                    ref={webcamRef}
                    audio={stage === "video"}
                    muted={true}
                    audioConstraints={{
                      echoCancellation: true,
                      noiseSuppression: true,
                      autoGainControl: true,
                    }}
                    onPlay={handleVideoOnPlay}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{facingMode: "user"}}
                    style={{width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)"}}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "55%",
                      height: "70%",
                      border: `4px dashed ${isFaceAligned ? "green" : "red"}`,
                      borderRadius: "50%",
                      boxSizing: "border-box",
                      transition: "border-color 0.3s",
                      pointerEvents: "none",
                    }}
                  />
                </>
              )}
            </div>

          </div>

          {!capturedImage && !capturedVideo && (
            <div style={{marginTop: "15px", color: isFaceAligned ? "green" : "red", fontWeight: "bold", textAlign: "center"}}>
              {isFaceAligned ? i18next.t("general:Your face position is appropriate") : i18next.t("general:Please adjust your face in the frame")}
            </div>
          )}

          <div style={{marginTop: "20px", display: "flex", gap: "10px", alignItems: "center", justifyContent: "center", flexWrap: "wrap", minHeight: "80px", width: "100%"}}>
            {stage === "image" && (
              <>
                {!capturedImage ? (
                  <Button style={iphoneButtonStyles.outer} onClick={captureImage} disabled={!isFaceAligned}>
                    <div style={{...iphoneButtonStyles.innerImage, opacity: isFaceAligned ? 1 : 0.5}} />
                  </Button>
                ) : (
                  <>
                    <Button onClick={retakeImage}>{i18next.t("general:Retry")}</Button>
                    <Button type="primary" onClick={() => setStage("video")}>
                      {i18next.t("general:Upload and continue")}
                    </Button>
                  </>
                )}
              </>
            )}

            {stage === "video" && (
              <>
                {!capturedVideo ? (
                  <Button
                    style={iphoneButtonStyles.outer}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={!isFaceAligned && !isRecording}
                  >
                    <div style={isRecording ? iphoneButtonStyles.innerRecording : iphoneButtonStyles.innerVideo} />
                  </Button>
                ) : (
                  <>
                    <Button onClick={retakeVideo} color="danger">ضبط مجدد</Button>
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FaceCapturing;
